import React, { useEffect, useState, createContext, useContext } from 'react';
import { supabase } from '../lib/supabase';
import { User as SupabaseUser } from '@supabase/supabase-js';
type User = {
  id: string;
  name: string;
  email: string;
  isAdmin: boolean;
};
type UserContextType = {
  user: User | null;
  users: User[];
  loading: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  signup: (email: string, password: string, name: string, isAdmin?: boolean) => Promise<boolean>;
  addUser: (user: {
    name: string;
    email: string;
    password: string;
    isAdmin: boolean;
  }) => Promise<void>;
  deleteUser: (id: string) => Promise<void>;
};
const UserContext = createContext<UserContextType | undefined>(undefined);
export const UserProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  // Load all profiles
  const loadUsers = async () => {
    try {
      const {
        data,
        error
      } = await supabase.from('profiles').select('id, name, email, is_admin').order('name');
      if (error) {
        console.error('Error loading users:', error);
        throw error;
      }
      console.log('Loaded users from Supabase:', data); // Debug log
      const formattedUsers = data.map(u => ({
        id: u.id,
        name: u.name,
        email: u.email,
        isAdmin: u.is_admin
      }));
      setUsers(formattedUsers);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };
  // Load current user profile
  const loadUserProfile = async (authUser: SupabaseUser) => {
    try {
      const {
        data,
        error
      } = await supabase.from('profiles').select('id, name, email, is_admin').eq('id', authUser.id).single();
      if (error) throw error;
      const userProfile: User = {
        id: data.id,
        name: data.name,
        email: data.email,
        isAdmin: data.is_admin
      };
      setUser(userProfile);
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
  };
  useEffect(() => {
    const initializeAuth = async () => {
      console.log('UserContext: Starting initialization...'); // Debug log
      // Check for existing session
      const {
        data: {
          session
        }
      } = await supabase.auth.getSession();
      console.log('UserContext: Session check:', session ? 'Session found' : 'No session'); // Debug log
      if (session?.user) {
        console.log('UserContext: Loading user profile for:', session.user.email); // Debug log
        await loadUserProfile(session.user);
      }
      // Load all users
      console.log('UserContext: Loading all users...'); // Debug log
      await loadUsers();
      setLoading(false);
      console.log('UserContext: Initialization complete'); // Debug log
      // Listen for auth changes
      const {
        data: {
          subscription
        }
      } = supabase.auth.onAuthStateChange(async (event, session) => {
        console.log('UserContext: Auth state changed:', event); // Debug log
        if (session?.user) {
          await loadUserProfile(session.user);
        } else {
          setUser(null);
        }
      });
      return () => {
        subscription.unsubscribe();
      };
    };
    initializeAuth();
  }, []);
  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      const {
        data,
        error
      } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      if (error) {
        console.error('Login error:', error);
        return false;
      }
      if (data.user) {
        await loadUserProfile(data.user);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };
  const logout = async () => {
    try {
      await supabase.auth.signOut();
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };
  const signup = async (email: string, password: string, name: string, isAdmin: boolean = false): Promise<boolean> => {
    try {
      const {
        data,
        error
      } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name,
            is_admin: isAdmin
          }
        }
      });
      if (error) {
        console.error('Signup error:', error);
        return false;
      }
      // Reload users list
      await loadUsers();
      return true;
    } catch (error) {
      console.error('Signup error:', error);
      return false;
    }
  };
  const addUser = async (newUser: {
    name: string;
    email: string;
    password: string;
    isAdmin: boolean;
  }) => {
    try {
      // Use signup to create the user
      await signup(newUser.email, newUser.password, newUser.name, newUser.isAdmin);
    } catch (error) {
      console.error('Error adding user:', error);
      throw error;
    }
  };
  const deleteUser = async (id: string) => {
    try {
      // Delete profile (auth user will be handled by cascade)
      const {
        error
      } = await supabase.from('profiles').delete().eq('id', id);
      if (error) throw error;
      // Reload users
      await loadUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  };
  return <UserContext.Provider value={{
    user,
    users,
    loading,
    login,
    logout,
    signup,
    addUser,
    deleteUser
  }} data-id="element-467">
      {children}
    </UserContext.Provider>;
};
export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};